import React, { useMemo, useEffect } from 'react';
import { LineChart } from 'lucide-react';
import { TrendSparkline } from '../Dashboard/DashboardTrendCharts';
import { getFacilityAssessmentTrendSeries, getResidentAssessmentTrendSeries } from '../../data/assessmentTrendSeries';

const RESIDENT_CHART_DEFS = [
  {
    key: 'cognitiveMmse',
    title: 'Cognitive (MMSE-style)',
    subtitle: 'Score trend — last 6 review points (demo)',
    yMin: 0,
    yMax: 30,
    formatValue: (v) => `${v} / 30`,
    strokeClass: 'stroke-indigo-500',
    areaFill: 'rgb(99 102 241 / 0.14)',
    pointStroke: '#6366f1',
    ariaLabel: 'Cognitive assessment score trend',
  },
  {
    key: 'depressionGds',
    title: 'Depression (GDS-style)',
    subtitle: 'Score trend — last 6 review points (demo)',
    yMin: 0,
    yMax: 15,
    formatValue: (v) => `${v} / 15`,
    strokeClass: 'stroke-violet-500',
    areaFill: 'rgb(139 92 246 / 0.14)',
    pointStroke: '#8b5cf6',
    ariaLabel: 'Depression screening score trend',
  },
  {
    key: 'delirium4at',
    title: 'Delirium (4AT total)',
    subtitle: 'Score trend — last 6 screens (demo)',
    yMin: 0,
    yMax: 12,
    formatValue: (v) => `${v} / 12`,
    strokeClass: 'stroke-rose-500',
    areaFill: 'rgb(244 63 94 / 0.14)',
    pointStroke: '#f43f5e',
    ariaLabel: 'Four AT delirium screen total trend',
  },
  {
    key: 'updrs',
    title: 'UPDRS (Parkinson)',
    subtitle: 'Motor / functional trend (demo)',
    yMin: 0,
    yMax: 108,
    formatValue: (v) => `${v} / 108`,
    strokeClass: 'stroke-sky-500',
    areaFill: 'rgb(14 165 233 / 0.14)',
    pointStroke: '#0ea5e9',
    ariaLabel: 'UPDRS assessment trend',
  },
  {
    key: 'kidneyEgfr',
    title: 'Kidney function (eGFR proxy)',
    subtitle: 'Clinical score trend (demo)',
    yMin: 40,
    yMax: 100,
    formatValue: (v) => `${v}`,
    strokeClass: 'stroke-teal-500',
    areaFill: 'rgb(20 184 166 / 0.14)',
    pointStroke: '#14b8a6',
    ariaLabel: 'Kidney function trend',
  },
  {
    key: 'prostateScore',
    title: 'Prostate monitoring',
    subtitle: 'Stable disease marker (demo)',
    yMin: 0,
    yMax: 10,
    formatValue: (v) => `${v} / 10`,
    strokeClass: 'stroke-slate-500',
    areaFill: 'rgb(100 116 139 / 0.14)',
    pointStroke: '#64748b',
    ariaLabel: 'Prostate monitoring trend',
  },
  {
    key: 'rheumatoidDas',
    title: 'Rheumatoid activity (DAS-style)',
    subtitle: 'Disease activity trend (demo)',
    yMin: 0,
    yMax: 6,
    formatValue: (v) => `${Number(v).toFixed(1)}`,
    strokeClass: 'stroke-orange-500',
    areaFill: 'rgb(249 115 22 / 0.14)',
    pointStroke: '#f97316',
    ariaLabel: 'Rheumatoid disease activity trend',
  },
];

/**
 * @param {{
 *   mode?: 'resident' | 'facility',
 *   residentId?: string | number,
 *   facilityKey?: string,
 *   phase2Heading?: boolean,
 * }} props
 */
export default function AssessmentTrendCharts({
  mode = 'resident',
  residentId,
  facilityKey = 'all',
  phase2Heading = false,
}) {
  const facilitySeries = useMemo(() => getFacilityAssessmentTrendSeries(facilityKey), [facilityKey]);

  const residentSeries = useMemo(() => getResidentAssessmentTrendSeries(residentId ?? 0), [residentId]);

  const residentCharts = useMemo(() => {
    if (mode !== 'resident') return [];
    const labels = residentSeries.labels;
    return RESIDENT_CHART_DEFS.filter((def) => {
      const arr = residentSeries[def.key];
      return Array.isArray(arr) && arr.length >= 2;
    }).map((def) => ({
      ...def,
      values: residentSeries[def.key],
      xLabels: labels,
    }));
  }, [mode, residentSeries]);

  useEffect(() => {
    if (process.env.NODE_ENV !== 'development') return;
    // eslint-disable-next-line no-console
    console.debug('[AssessmentTrendCharts] series', {
      mode,
      residentId,
      facilityKey,
      residentChartCount: residentCharts.length,
      facility: mode === 'facility' ? facilitySeries : null,
    });
  }, [mode, residentId, facilityKey, residentCharts.length, facilitySeries]);

  if (mode === 'facility') {
    return (
      <div className="mt-6 pt-6 border-t border-gray-100">
        <div className="flex items-center gap-2 mb-4">
          <LineChart className="w-5 h-5 text-indigo-600 shrink-0" aria-hidden />
          <div>
            <h3 className="mf-heading-sm text-gray-900">
              {phase2Heading ? 'Phase 2 — Facility assessment trends' : 'Facility assessment trends'}
            </h3>
            <p className="mf-body text-sm text-gray-500">
              Completed vs overdue counts by day — synthetic roll-up for dashboard / clinical overview (demo).
            </p>
          </div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <TrendSparkline
            title="Assessments completed (7-day)"
            subtitle={`Facility key: ${facilityKey}`}
            values={facilitySeries.assessmentsCompleted}
            yMin={0}
            yMax={40}
            formatValue={(v) => `${v} completed`}
            strokeClass="stroke-indigo-500"
            areaFill="rgb(99 102 241 / 0.14)"
            pointStroke="#6366f1"
            xLabels={facilitySeries.labels}
            ariaLabel="Seven day count of assessments completed"
          />
          <TrendSparkline
            title="Overdue assessments (7-day)"
            subtitle="Items past regulatory due date (demo)"
            values={facilitySeries.overdueAssessments}
            yMin={0}
            yMax={16}
            formatValue={(v) => `${v} overdue`}
            strokeClass="stroke-amber-500"
            areaFill="rgb(245 158 11 / 0.16)"
            pointStroke="#f59e0b"
            xLabels={facilitySeries.labels}
            ariaLabel="Seven day count of overdue assessments"
          />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border-2 border-indigo-100 shadow-lg p-6">
      <div className="flex items-center gap-2 mb-4">
        <LineChart className="w-5 h-5 text-indigo-600 shrink-0" aria-hidden />
        <div>
          <h3 className="text-lg font-bold text-gray-900">Assessment trend charts</h3>
          <p className="text-sm text-gray-600">
            Repeatable scores over time (demo history). Wire to assessment instances when the form engine persists results.
          </p>
        </div>
      </div>
      {residentCharts.length === 0 ? (
        <p className="text-sm text-gray-500">No trend series available for this resident.</p>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
          {residentCharts.map((c) => (
            <TrendSparkline
              key={c.key}
              title={c.title}
              subtitle={c.subtitle}
              values={c.values}
              yMin={c.yMin}
              yMax={c.yMax}
              formatValue={c.formatValue}
              strokeClass={c.strokeClass}
              areaFill={c.areaFill}
              pointStroke={c.pointStroke}
              xLabels={c.xLabels}
              ariaLabel={c.ariaLabel}
            />
          ))}
        </div>
      )}
    </div>
  );
}
